import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

// api / redux
import AppApi from '../../api/app.api';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { setCampus, setSchool, setTermSchool, setViewType } from '../../store/reducers/app';

// constants
import { mainListItems, manageListItems, roomListItems, reportListItems, publicSiteLiteItems, downloadsItems, eventListItems, hasRouteAccess } from '../../constants/routes';
import { ACADEMIC_VIEW_TYPE, accountTypes, CENTRAL_TIMETABLER_VIEW_TYPE, FACILITIES_VIEW_TYPE, viewTypeArr } from '../../constants';
import { colors, theme } from '../../theme';

// comps
import { AppBar, Divider, Drawer, IconButton, List, ListItem, ListItemText, Toolbar, Typography } from '@mui/material';
import MenuDropdown from './menuDropdown';
import DateStampBox from './dateStampBox';
import QuickSearch from './quickSearch';

// icons
import AccountIcon from '@mui/icons-material/AccountCircle';
import ExitToApp from '@mui/icons-material/ExitToApp';
import { ReactComponent as SchoolIcon } from '../../assets/icons/school-icon.svg';
import { ReactComponent as MapIcon } from '../../assets/icons/map-icon.svg';

//helpers
import { authenticationService } from '../../services/authenticationService.service';
import { isUserTypeCentralTimetabler, isUserTypeFacilities } from '../../helpers/user';

export const AppBarState = React.createContext(true);

const MainLayout = (props: any) => {
    const dispatch = useAppDispatch();
    let navigate = useNavigate();
    const _appApi = new AppApi();

    const user = useAppSelector((state) => state.user);
    const appState = useAppSelector((state) => state.appState);
    const isSuperAdmin = user.accountType === accountTypes.superAdmin;
    const isAdmin = user.accountType === accountTypes.admin;

    const { campuses, campus, schools, school, termSchools, viewType } = appState;
    const viewTypes = viewTypeArr.filter((item) => item.type !== appState?.viewType);
    const isUserCentralTimetabler = isUserTypeCentralTimetabler(user, viewType);
    const isUserFacilities =  isUserTypeFacilities(user, viewType);


    const path = useLocation().pathname;

    const [state, setState] = useState({
        open: true,
        schools: [],
        terms: [],
        campuses: [],
        viewTypes: [],
        dataSourceStatus: null,
    });

    const handleViewTypeChange = (event: any) => {
        const {
            target: { value },
        } = event;

        const viewType = value?.type;

        dispatch(setViewType(viewType));
        navigate('/');
    };

    const handleSchoolChange = (event: any) => {
        const {
            target: { value },
        } = event;

        dispatch(setSchool(value));
    };

    const handleTermChange = (event: any) => {
        const {
            target: { value },
        } = event;

        dispatch(setTermSchool(value));
    };

    const handleCampusChange = (event: any) => {
        const {
            target: { value },
        } = event;

        dispatch(setCampus(value));
    };

    const loadData = async () => {
        const dataSourceStatus = await _appApi.GetDataSourceStatus();
        setState({
            ...state,
            dataSourceStatus,
        });
    };

    useEffect(() => {
        loadData().catch((error) => toast.error('Error fetching data update'));

        setState({
            ...state,
            schools: schools,
            campuses: campuses,
            viewTypes: viewTypes,
        });
    }, [appState]);

    //only display the terms that relate to the currently selected school
    const filteredTermSchools = termSchools.filter((item) => item.schoolId === school?.id);
    const termSchool = filteredTermSchools.find((item) => item.termSchoolId === appState.term?.termSchoolId);

    const filterMenuItems = (items: Array<any>) => {
        return items.filter((item) => hasRouteAccess(item, viewType, user.accountType));
    };

    const handleExit = () => {
        authenticationService.logout();
        window.location.replace('https://login.bcit.ca/cas/logout');
    };

    return (
        <>
            <ImageContainer />
            <Container>
                <StyledAppBar position="absolute">
                    <Toolbar sx={{ paddingRight: 24, paddingLeft: 20 }}>
                        <QuickSearch sx={{ ml: '115px' }} />
                        <Header>
                            <DateStampBox dataSourceStatus={state.dataSourceStatus} />

                            {(isSuperAdmin && viewType) && (
                                <HeaderItem>
                                    <MenuDropdown
                                        icon={<SchoolIcon />}
                                        value={viewTypeArr.find((item) => item.type === viewType)}
                                        data={viewTypes}
                                        name={'label'}
                                        field={'type'}
                                        dataItemKey={'viewType'}
                                        onChange={handleViewTypeChange}
                                        singleOption={true}
                                    />
                                </HeaderItem>
                            )}

                            {(viewType === CENTRAL_TIMETABLER_VIEW_TYPE || viewType === FACILITIES_VIEW_TYPE) && (
                                <HeaderItem>
                                    {(isUserCentralTimetabler || isUserFacilities || isSuperAdmin) ? (
                                        <MenuDropdown icon={<MapIcon />} value={campus} data={campuses} name={'name'} field={'id'} onChange={handleCampusChange} />
                                    ) : (
                                        <>
                                            <MapIcon />
                                            <Typography>{campus && campus.name}</Typography>
                                        </>
                                    )}
                                </HeaderItem>
                            )}

                            {(isSuperAdmin || isAdmin) && viewType === ACADEMIC_VIEW_TYPE && (
                                <HeaderItem>
                                    <MenuDropdown icon={<MapIcon />} value={school} data={schools} name={'name'} field={'id'} onChange={handleSchoolChange} />
                                </HeaderItem>
                            )}

                            {/* every account type in academic view has ability to change terms */}
                            {viewType === ACADEMIC_VIEW_TYPE && (
                                <HeaderItem>
                                    <MenuDropdown value={termSchool} data={filteredTermSchools?.slice(0, 8)} name={'code'} field={'termSchoolId'} onChange={handleTermChange} />
                                </HeaderItem>
                            )}

                            <HeaderItem style={{ cursor: 'default' }}>
                                <AccountIcon />
                                <Typography>{user && user.firstName}</Typography>
                            </HeaderItem>

                            <HeaderItem>
                                <IconButton onClick={handleExit}>
                                    <ExitToApp />
                                </IconButton>
                            </HeaderItem>
                        </Header>
                    </Toolbar>
                </StyledAppBar>
                <StyledDrawer variant="permanent" open classes={{ paper: 'drawerPaper' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px', background: colors.navy.main, minHeight: '52px' }} />
                    {user.accountType && (
                        <>
                            <Divider />
                            <StyledList>
                                {filterMenuItems(mainListItems).map((item) => (
                                    <StyledLink to={item.route} key={item.title}>
                                        <StyledListItem button active={item.route === path}>
                                            <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                        </StyledListItem>
                                    </StyledLink>
                                ))}
                            </StyledList>

                            {filterMenuItems(eventListItems)?.length > 0 && (
                                <StyledList>
                                    {eventListItems.some((list) => list.accountType.includes(user.accountType)) && (
                                        <>
                                            <Divider />
                                            <SubHeader variant="h5">{'Events'}</SubHeader>
                                        </>
                                    )}
                                    {filterMenuItems(eventListItems).map((item) => (
                                        <StyledLink to={item.route} key={item.title}>
                                            <StyledListItem button active={item.route === path}>
                                                <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                            </StyledListItem>
                                        </StyledLink>
                                    ))}
                                </StyledList>
                            )}

                            {filterMenuItems(manageListItems)?.length > 0 && (
                                <StyledList>
                                    {manageListItems.some((list) => list.accountType.includes(user.accountType)) && (
                                        <>
                                            <Divider />
                                            <SubHeader variant="h5">{'Manage'}</SubHeader>
                                        </>
                                    )}
                                    {filterMenuItems(manageListItems).map((item) => (
                                        <StyledLink to={item.route} key={item.title}>
                                            <StyledListItem button active={item.route === path}>
                                                <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                            </StyledListItem>
                                        </StyledLink>
                                    ))}
                                </StyledList>
                            )}

                            <StyledList>
                                {roomListItems.some((list) => list.accountType.includes(user.accountType)) && (
                                    <>
                                        <Divider />
                                        <SubHeader variant="h5">{'Room'}</SubHeader>
                                    </>
                                )}
                                {filterMenuItems(roomListItems).map((item) => (
                                    <StyledLink to={item.route} key={item.title}>
                                        <StyledListItem button active={item.route === path}>
                                            <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                        </StyledListItem>
                                    </StyledLink>
                                ))}
                            </StyledList>

                            <StyledList>
                                {reportListItems.some((list) => list.accountType.includes(user.accountType)) && (
                                    <>
                                        <Divider />
                                        <SubHeader variant="h5">{'Reports'}</SubHeader>
                                    </>
                                )}
                                {filterMenuItems(reportListItems).map((item) => (
                                    <StyledLink to={item.route} key={item.title}>
                                        <StyledListItem button active={item.route === path}>
                                            <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                        </StyledListItem>
                                    </StyledLink>
                                ))}
                            </StyledList>
                            {filterMenuItems(downloadsItems)?.length > 0 && (
                                <StyledList>
                                    {downloadsItems.some((list) => list.accountType.includes(user.accountType)) && (
                                        <>
                                            <Divider />
                                            <SubHeader variant="h5">{'Downloads'}</SubHeader>
                                        </>
                                    )}
                                    {filterMenuItems(downloadsItems).map((item) => (
                                        <StyledLink to={item.route} key={item.title}>
                                            <StyledListItem button active={item.route === path}>
                                                <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                            </StyledListItem>
                                        </StyledLink>
                                    ))}
                                </StyledList>
                            )}

                            {/* Public site list only for Academic View Type */}
                            {viewType === ACADEMIC_VIEW_TYPE && (
                                <>
                                    <StyledList>
                                        {publicSiteLiteItems.some((list) => list.accountType.includes(user.accountType)) && (
                                            <>
                                                <Divider />
                                                <SubHeader variant="h5">{'Public Site'}</SubHeader>
                                            </>
                                        )}
                                        {filterMenuItems(publicSiteLiteItems).map((item) => (
                                            <StyledLink to={item.route} key={item.title}>
                                                <StyledListItem button active={item.route === path}>
                                                    <ListItemText primary={item.title} sx={{ padding: '0 2px' }} />
                                                </StyledListItem>
                                            </StyledLink>
                                        ))}
                                    </StyledList>
                                </>
                            )}
                        </>
                    )}
                </StyledDrawer>
                <Content>
                    <AppBarSpacer>
                        <AppBarState.Provider value={state.open}>
                            <Outlet />
                        </AppBarState.Provider>
                    </AppBarSpacer>
                </Content>
            </Container>
        </>
    );
};

// TODO: add propTypes

export default MainLayout;

const Container = styled.div`
    display: flex;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
`;

const ImageContainer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 52px;
    background: ${colors.navy.main};
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: unset;
    padding: 5px 0px;
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex: 1;
    /* height: 52px; */
    margin-left: 24px;

    > div {
        display: flex;
    }
`;

const HeaderItem = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-left: 1em;

    svg {
        width: 22px;
        height: auto;
        margin-right: 10px;

        path {
            fill: ${colors.white};
        }
    }

    span {
        font-weight: 500;
        margin-right: 5px;
    }
`;

const StyledAppBar = styled(AppBar)`
    && {
        z-index: ${theme.zIndex.drawer} + 1;
        transition: ${theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        })};
        background: transparent;
        box-shadow: none;

        .appBarShift {
            margin-left: 130px;
            width: calc(100% - 130px);
            transition: ${theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            })};
        }

        .MuiToolbar-root {
            min-height: 52px;
        }
    }
`;

const StyledDrawer = styled(Drawer)`
    && {
        display: flex;
        flex-direction: column;
        overflow: auto;
        height: 100vh;

        .drawerPaper {
            position: relative;
            white-space: nowrap;
            width: 130px;
            transition: ${theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            })};
        }

        .drawerPaperClose {
            overflow-x: hidden;
            position: relative;
            transition: ${theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            })};
            width: ${theme.spacing(7)};
            /* [${theme.breakpoints.up('sm')}]: {
            width: ${theme.spacing(9)};
        };
    },  */
        }
    }
`;

const StyledList = styled(List)`
    && {
        padding: 5px 0px;
    }
`;

const StyledListItem = styled(({ active, ...rest }) => <ListItem {...rest} />)`
    && {
        padding: 3px 5px;
        ${({ active }) =>
            active &&
            `
      background: ${colors.navy.light};
      border-left: 3px solid ${colors.navy.main};
      padding: 3px 2px;

      p, span{
        color: ${colors.black};
      }

      &:hover {
        background: ${colors.navy.light};
        border-left: 3px solid ${colors.navy.main};

        p, span{
            color: ${colors.black};
        }
      }
      
    `}
    }
`;

const SubHeader = styled(Typography)`
    color: ${colors.navy.main} !important;
    padding: 7px 10px 5px 7px;
`;

const Content = styled.main`
    position: relative;
    flex-grow: 1;
    padding: 10px;
    max-width: calc(100vw - 130px);
    margin-top: 52px;
    /* min-height: calc(100vh - 64px); */
    background-color: ${colors.bg};
    overflow-x: hidden;
`;

const AppBarSpacer = styled.div`
    min-height: 80px !important;
`;
